import { authRoutes } from '@/features/auth/authRoutes.js'
import { authGuard } from '@/features/auth/guards/authGuard.js'
import { sessionGuard } from '@/features/auth/guards/sessionGuard.js'
import { clubLandingRoutes } from '@/features/clubLanding/clubLandingRoutes.js'
import { dashboardRoutes } from '@/features/dashboard/dashboardRoutes.js'
import { eventManagementRoutes } from '@/features/eventManagement/eventManagementRoutes.js'
import { useAppEnvironmentMode } from '@/features/environment/useAppEnvironmentMode.js'
import { createAdminSidebarMenuLinkItems } from '@/features/layout/components/constants/useAdminSidebarMenuLinks.js'
import DashboardLayout from '@/features/layout/DashboardLayout.vue'
import { mailerRoutes } from '@/features/mailer/mailerRoutes.js'
import { memRegRoutes } from '@/features/memReg/memRegRoutes.js'
import { TenantType } from '@/features/organization/organizationTypeName.js'
import { useAuthStore } from '@/features/auth/useAuthStore.js'
import { createNoAccessPath } from '@/features/noAccess/noAccess.utils.js'
import { decidePermission } from '@/features/permissions/permissionEngine.js'
import { notificationManagementRouter } from '@/features/notificationManagement/notificationManagementRouter.js'
import { peopleManagementRouter } from '@/features/peopleManagement/peopleManagementRouter.js'
import { profileManagementRoutes } from '@/features/profileManagement/profileManagementRoutes.js'
import { clubRegisteredGuard } from '@/features/router/guards/clubRegisteredGuard.js'
import { getActiveClubId, useActiveClub } from '@/features/router/useActiveClub.js'
import { mobileAppManagementRouter } from '@/features/settingsManagement/mobileAppManagement/mobileAppManagementRouter.js'
import { settingsManagementRoutes } from '@/features/settingsManagement/settingsManagementRoutes.js'
import { siteRoutes } from '@/features/site/siteRoutes.js'
import { tenantCodesRoutes } from '@/features/superAdmin/tenantCodes/tenantCodesRoutes.js'
import { tenantGroupsRoutes } from '@/features/superAdmin/tenantGroups/tenantGroupsRoutes.js'
import { tenantTermGroupsRoutes } from '@/features/superAdmin/tenantTermGroups/tenantTermGroupsRoutes.js'
import { tenantTermsRoutes } from '@/features/superAdmin/tenantTerms/tenantTermsRoutes.js'
import { trackingRoutes } from '@/features/tracking/trackingRoutes.js'
import NotFoundView from '@/features/views/NotFound/NotFoundView.vue'

import { adminGuard } from './guards/adminGuard'

const { isUat2Mode } = useAppEnvironmentMode()

function getAdminRootRedirectCandidates() {
  const { activeClub } = useActiveClub()
  const canShowTenantManagementLinks = activeClub?.tenantType === TenantType.Club && isUat2Mode

  return createAdminSidebarMenuLinkItems({ canShowTenantManagementLinks }).flatMap(item => {
    if (!item.to) return []

    const actions =
      item.permissionAnyActions || (item.permissionAction ? [item.permissionAction] : [])
    return actions.map(action => [action, item.to])
  })
}

function preserveRouteQuery(path, to) {
  const query = to?.query ?? {}
  const queryEntries = Object.entries(query).filter(([, value]) => value !== undefined)

  if (!queryEntries.length) return path

  return {
    path,
    query: Object.fromEntries(queryEntries),
  }
}

function resolveAdminRootRedirect(to) {
  const authStore = useAuthStore()
  const { activeClub } = useActiveClub()
  const routeActiveClubId = Array.isArray(to?.query?.activeClubId)
    ? to.query.activeClubId[0]
    : to?.query?.activeClubId
  const activeTenantId = routeActiveClubId || getActiveClubId()

  for (const [action, path] of getAdminRootRedirectCandidates()) {
    const decision = decidePermission(action, {
      myDetails: authStore.myDetails,
      activeTenantId,
      activeTenant: activeClub,
      surface: 'admin',
      route: '/admin',
    })

    if (decision.allowed) return preserveRouteQuery(path, to)
  }

  return createNoAccessPath({
    reason: 'permission-denied',
    action: 'admin',
    returnUrl: to.fullPath,
  })
}

export const clubRoutes = [
  ...clubLandingRoutes,
  {
    path: 'admin',
    beforeEnter: [authGuard, sessionGuard, clubRegisteredGuard, adminGuard],
    component: DashboardLayout,
    children: [
      {
        path: '',
        beforeEnter: resolveAdminRootRedirect,
        component: NotFoundView,
      },
      ...dashboardRoutes,
      ...eventManagementRoutes,
      ...settingsManagementRoutes,
      ...profileManagementRoutes,
      ...peopleManagementRouter,
      ...trackingRoutes,
      ...mailerRoutes,
      ...notificationManagementRouter,
      ...siteRoutes,
      ...(isUat2Mode ? tenantCodesRoutes : []),
      ...(isUat2Mode ? tenantGroupsRoutes : []),
      ...(isUat2Mode ? tenantTermsRoutes : []),
      ...(isUat2Mode ? tenantTermGroupsRoutes : []),
      ...mobileAppManagementRouter,

      {
        path: ':pathMatch(.*)*',
        name: 'club-not-found',
        component: NotFoundView,
      },
    ],
  },
  ...authRoutes,
  ...memRegRoutes,
]
